import { type Quantity, quantity } from "../money/money.ts";
import { DEFAULT_ROUNDING, divideRounded, pow10, type RoundingMode } from "../money/rounding.ts";

/**
 * Stock quantities (`11-inventory.md` § 3, § 10).
 *
 * The same treatment money gets, for the same reason. A supply is bought in
 * one unit and consumed in another — a keg by the barrel, drawn by the
 * millilitre — and the factor between them is arbitrary. Held as a float,
 * 0.1 kg + 0.2 kg is not 0.3 kg, and the error lands in a cost report that
 * somebody prices a menu from.
 *
 * So a stock quantity is a `bigint` of thousandths, and a conversion factor is
 * a `bigint` on the same scale. Three decimal places covers grams against
 * kilograms and millilitres against litres, which is the resolution the
 * verified unit list implies.
 *
 * This is the **storage** form of the `Quantity` in `../money/money.ts`, which
 * carries its own scale because an order line's quantity comes from whatever a
 * cashier typed. A column cannot carry a per-row scale, so stock fixes one —
 * and `toQuantity` bridges back, so a recipe amount can go straight into the
 * pricing engine without a second conversion story growing up beside this one.
 */
export const QUANTITY_SCALE = 3;

const DIVISOR = pow10(QUANTITY_SCALE);

/** Parses "1.5" into the scaled integer a stock row holds. */
export function parseStockQuantity(decimal: string): bigint {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(decimal.trim());
  if (!match) throw new SyntaxError(`parseStockQuantity: not a decimal: ${decimal}`);
  const [, sign, whole = "0", fraction = ""] = match;
  if (fraction.length > QUANTITY_SCALE) {
    throw new RangeError(`parseStockQuantity: ${decimal} exceeds ${QUANTITY_SCALE} decimal places`);
  }
  return BigInt(`${sign}${whole}${fraction.padEnd(QUANTITY_SCALE, "0")}`);
}

/** The inverse. A whole number formats without a decimal point. */
export function formatStockQuantity(value: bigint): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const whole = abs / DIVISOR;
  const fraction = (abs % DIVISOR).toString().padStart(QUANTITY_SCALE, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

/**
 * Converts `quantity` through `factor`, where `factor` is how many of the
 * target unit make one of the source — 1 L = 1000 ml is `parseStockQuantity("1000")`.
 *
 * Rounding is explicit and defaults to the project's half-up, because a
 * conversion that does not divide evenly has to land somewhere, and silently
 * truncating always costs the merchant stock they paid for.
 */
export function convertQuantity(
  value: bigint,
  factor: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  if (factor <= 0n) throw new RangeError("convertQuantity: factor must be positive");
  return divideRounded(value * factor, DIVISOR, mode);
}

/** The other direction: from the target unit back to the source. */
export function unconvertQuantity(
  value: bigint,
  factor: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  if (factor <= 0n) throw new RangeError("unconvertQuantity: factor must be positive");
  return divideRounded(value * DIVISOR, factor, mode);
}

/** The bridge to the pricing engine's own quantity type. */
export function toQuantity(value: bigint): Quantity {
  return quantity(value, QUANTITY_SCALE);
}

/**
 * The unit cost of a batch, given what was paid and what arrived.
 *
 * `freeQuantity` is why this is a function rather than a division at the call
 * site. Twelve bottles paid for and one free is thirteen bottles at twelve
 * bottles' cost *(verified — "Free Quantity" sits beside "Purchase Quantity")*,
 * and dividing by the paid quantity would overstate the cost of every
 * promotional delivery.
 */
export function batchUnitCost(
  totalCost: bigint,
  paidQuantity: bigint,
  freeQuantity = 0n,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  const received = paidQuantity + freeQuantity;
  if (received <= 0n) throw new RangeError("batchUnitCost: nothing was received");
  return divideRounded(totalCost * DIVISOR, received, mode);
}

/**
 * What `quantity` of stock costs at `unitCost` per whole unit.
 *
 * The quantity carries `QUANTITY_SCALE` and the cost does not, so the product
 * carries the scale and has to come back out. Doing that here rather than at
 * each call site is the point: it is the multiplication most likely to be
 * written once correctly and then copied wrongly.
 */
export function costOf(
  value: bigint,
  unitCost: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): bigint {
  return divideRounded(value * unitCost, DIVISOR, mode);
}
